'use client'

import {
  useState,
  useRef,
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  BarChart3,
  LineChart,
  PieChart,
  User,
  ArrowUpRight,
  Download,
  Pin,
  ExternalLink,
  FileText,
  LogOut,
  Package,
  Settings,
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { updatePreferences, createPreferences } from '@/features/preferences/api/preferencesService'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { authService } from '@/lib/authService'
import { OrgSelectorMenu } from '@/features/orgs/components/OrgSelectorMenu'
import { useTranslation } from 'react-i18next'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useMobileNav } from '@/shared/contexts/MobileNavContext'

type NavigationChild = {
  id: string
  url: string
  translationKey: string
}

type NavigationItem = {
  id: string
  url: string
  icon: typeof BarChart3
  translationKey: string
  children?: readonly NavigationChild[]
}

const navigationData = {
  navItems: [
    {
      id: 'dashboard',
      url: '/dashboard',
      icon: BarChart3,
      translationKey: 'sidebar.dashboard',
    },
{
      id: 'products',
      url: '/dashboard/management/products',
      icon: Package,
      translationKey: 'sidebar.products',
    },
    {
      id: 'indirect-costs',
      url: '/dashboard/indirect-costs',
      icon: PieChart,
      translationKey: 'sidebar.indirectCosts',
      children: [],
    },
  ] as const satisfies readonly NavigationItem[],
  bottomActions: [
    { icon: User, translationKey: 'bottomActions.account' },
    { icon: ArrowUpRight, translationKey: 'bottomActions.upgrade' },
    { icon: Download, translationKey: 'bottomActions.install' },
  ] as const,
} as const

/**
 * Compact application sidebar that exposes navigation shortcuts and the user menu.
 *
 * @component
 * @returns {ReactElement} Sidebar element rendered on the left edge of the layout.
 * @example
 * ```tsx
 * <AppSidebar />
 * ```
 */
export function AppSidebar(): ReactElement {
  const { t: tCommon, i18n } = useTranslation('common')
  const { preferredLanguageMissing, markLanguageAsSet } = useActiveOrg()
  const { t: tNavigation } = useTranslation('navigation')
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const { isOpen: mobileOpen, setIsOpen: setMobileOpen } = useMobileNav()
  // Animation state - tracks if we're in exit animation
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)
  const lastHoveredRef = useRef<string | null>(null)
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const flyoutRef = useRef<HTMLDivElement | null>(null)
  const animationTimerRef = useRef<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Derive whether panel should be visible based on hoveredItem
  const currentNavItem = navigationData.navItems.find(item => item.id === hoveredItem) as
    | NavigationItem
    | undefined
  const shouldShowPanel = Boolean(
    hoveredItem && currentNavItem?.children && currentNavItem.children.length > 0
  )

  // Handle animation state changes via event handler pattern
  const startExitAnimation = (): void => {
    // Clear any existing timer
    if (animationTimerRef.current) {
      window.clearTimeout(animationTimerRef.current)
    }
    setIsAnimatingOut(true)
    // Schedule animation end
    animationTimerRef.current = window.setTimeout(() => {
      setIsAnimatingOut(false)
      animationTimerRef.current = null
    }, 300)
  }

  const cancelExitAnimation = (): void => {
    if (animationTimerRef.current) {
      window.clearTimeout(animationTimerRef.current)
      animationTimerRef.current = null
    }
    setIsAnimatingOut(false)
  }

  // Show panel while content is visible OR during exit animation
  const showPanel = shouldShowPanel || isAnimatingOut

  const handleNavigation = (url: string): void => {
    navigate(url)
  }

  const handleItemHover = (itemId: string): void => {
    // Cancel any exit animation when re-entering
    cancelExitAnimation()
    setHoveredItem(itemId)
    lastHoveredRef.current = itemId
  }

  const isWithinInteractiveRegion = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) {
      return false
    }

    return Boolean(sidebarRef.current?.contains(target) || flyoutRef.current?.contains(target))
  }

  const handleInteractiveAreaLeave = (event: ReactMouseEvent<HTMLElement>): void => {
    if (isWithinInteractiveRegion(event.relatedTarget)) {
      return
    }

    // Start exit animation if panel was showing
    if (shouldShowPanel) {
      startExitAnimation()
    }
    setHoveredItem(null)
  }

  const resolvedLanguage = i18n.resolvedLanguage ?? i18n.language
  const languageOptions = [
    { code: 'en', labelKey: 'userMenu.english' },
    { code: 'pl', labelKey: 'userMenu.polish' },
  ] as const

  const handleLanguageChange = async (
    language: (typeof languageOptions)[number]['code']
  ): Promise<void> => {
    if (resolvedLanguage?.startsWith(language)) {
      return
    }

    try {
      if (preferredLanguageMissing) {
        await createPreferences({ preferredLanguage: language as string })
      } else {
        await updatePreferences({ preferredLanguage: language as string })
      }
      void i18n.changeLanguage(language)
      markLanguageAsSet()
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 400) {
        toast.error('Invalid language tag')
      } else {
        toast.error('Failed to update language preference')
      }
    }
  }

  const handleMobileNavigation = (url: string): void => {
    setMobileOpen(false)
    navigate(url)
  }

  return (
    <>
      {/* Mobile navigation drawer - controlled via MobileNavContext */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 glass bg-[var(--glass-surface-dark)] p-0">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <img
                  src="/ab-black-white-pill-logo.webp"
                  alt={tCommon('appHeader.logoAlt')}
                  className="h-full w-full object-cover"
                />
              </div>
              <span className="text-lg font-semibold">AbyssBlock</span>
            </SheetTitle>
          </SheetHeader>

          {/* Mobile Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationData.navItems.map(item => {
              const label = tNavigation(`${item.translationKey}.label`)
              const isActive = location.pathname === item.url
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleMobileNavigation(item.url)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'border border-border bg-transparent text-foreground'
                      : 'hover:bg-accent/50 text-muted-foreground'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              )
            })}
          </nav>

          {/* Mobile User Section */}
          <div className="mt-auto border-t border-border p-4 space-y-2">
            {/* Language Selection */}
            <div className="px-3 py-2">
              <span className="text-xs font-normal text-muted-foreground">{tCommon('userMenu.language')}</span>
              <div className="flex gap-2 mt-2">
                {languageOptions.map(option => {
                  const isActive = resolvedLanguage?.startsWith(option.code) ?? false
                  return (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => !isActive && handleLanguageChange(option.code)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'border border-border bg-transparent text-foreground'
                          : 'hover:bg-accent/50 text-muted-foreground'
                      }`}
                    >
                      {tCommon(option.labelKey)}
                    </button>
                  )
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleMobileNavigation('/dashboard/invoices')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-accent/50 text-muted-foreground transition-colors"
            >
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">{tNavigation('sidebar.invoices.label')}</span>
            </button>
            <button
              type="button"
              onClick={() => handleMobileNavigation('/dashboard/orders')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-accent/50 text-muted-foreground transition-colors"
            >
              <LineChart className="w-5 h-5" />
              <span className="text-sm font-medium">{tNavigation('sidebar.orders.label')}</span>
            </button>
            <button
              type="button"
              onClick={() => handleMobileNavigation('/dashboard/settings')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-accent/50 text-muted-foreground transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span className="text-sm font-medium">{tNavigation('sidebar.settings.label')}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false)
                authService.logout()
                navigate('/', { replace: true })
              }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-accent/50 text-muted-foreground transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">{tCommon('userMenu.logOut')}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar - hidden on mobile/tablet */}
      <div className="relative hidden lg:flex">
        <div
          ref={sidebarRef}
          className="!bg-transparent glass h-screen w-22 flex flex-col fixed left-0 top-0 z-[60]"
        >
        {/* Logo */}
        <div className="p-4 flex items-center justify-center">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            aria-label={tCommon('appHeader.logoAlt')}
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              src="/ab-black-white-pill-logo.webp"
              alt={tCommon('appHeader.logoAlt')}
              className="h-full w-full object-cover"
            />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-2 flex flex-col justify-center">
          {navigationData.navItems.map(item => {
            const label = tNavigation(`${item.translationKey}.label`)
            const navItem = item as NavigationItem
            const hasChildren = Boolean(navItem.children && navItem.children.length > 0)
            const isActive = location.pathname === item.url
            return (
              <div
                key={item.id}
                className="mb-4 relative"
                onMouseEnter={() => handleItemHover(item.id)}
                onMouseLeave={handleInteractiveAreaLeave}
              >
                <div
                  className="flex flex-col items-center px-2 py-3 rounded-lg cursor-pointer glass-button !bg-transparent hover:!bg-glass-light dark:hover:!bg-glass-dark transition-colors"
                  onClick={() => handleNavigation(item.url)}
                >
                  <item.icon className={`w-6 h-6 mb-1 transition-opacity ${
                    isActive ? '' : 'opacity-50'
                  }`} />
                  <span className="text-xs text-muted-foreground text-center">{label}</span>
                </div>

                {hasChildren && (
                  <div
                    aria-hidden
                    className="absolute -right-2 top-0 h-full w-3 z-[60]"
                    onMouseEnter={() => handleItemHover(item.id)}
                    onMouseLeave={handleInteractiveAreaLeave}
                  />
                )}
              </div>
            )
          })}
        </nav>

        {/* User Section - Compact */}
        <div className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                id="user-menu-trigger"
                type="button"
                aria-label="User menu"
                className="w-full flex flex-col items-center px-2 py-3 rounded-lg cursor-pointer glass-button !bg-transparent hover:!bg-glass-light dark:hover:!bg-glass-dark transition-colors"
              >
                <User className="w-4 h-4 mb-1" />
                <span className="text-xs text-muted-foreground text-center">{tCommon('userMenu.label')}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="end"
              sideOffset={8}
              className="min-w-40 rounded-lg"
            >
              {/* Organization selector */}
              <OrgSelectorMenu />
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {tCommon('userMenu.language')}
              </DropdownMenuLabel>
              {languageOptions.map(option => {
                const isActive = resolvedLanguage?.startsWith(option.code) ?? false
                return (
                  <DropdownMenuItem
                    key={option.code}
                    role="menuitemradio"
                    aria-checked={isActive}
                    className={isActive ? 'bg-accent' : ''}
                    onClick={() => !isActive && handleLanguageChange(option.code)}
                  >
                    {tCommon(option.labelKey)}
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/invoices')}>
                <FileText className="mr-2 h-4 w-4" />
                {tNavigation('sidebar.invoices.label')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/dashboard/orders')}>
                <LineChart className="mr-2 h-4 w-4" />
                {tNavigation('sidebar.orders.label')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                {tNavigation('sidebar.settings.label')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  authService.logout()
                  navigate('/', { replace: true })
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {tCommon('userMenu.logOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Flyout Panel */}
      {showPanel && hoveredItem && (
        <div
          ref={flyoutRef}
          className={`fixed left-22 -ml-px top-0 !bg-transparent glass w-64 h-screen z-[60] transition-all duration-200 ease-out ${
            isAnimatingOut
              ? 'opacity-0 -translate-x-2'
              : 'opacity-100 translate-x-0 animate-in slide-in-from-left-2'
          }`}
          onMouseEnter={() => {
            if (lastHoveredRef.current) {
              setHoveredItem(lastHoveredRef.current)
            }
          }}
          onMouseLeave={handleInteractiveAreaLeave}
        >
          {(() => {
            const currentItem = navigationData.navItems.find(item => item.id === hoveredItem) as
              | NavigationItem
              | undefined
            if (!currentItem || !currentItem.children || currentItem.children.length === 0) {
              return null
            }
            const itemTitle = tNavigation(`${currentItem.translationKey}.title`, {
              defaultValue: tNavigation(`${currentItem.translationKey}.label`),
            })

            return (
              <>
                {/* Header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sidebar-foreground font-medium">{itemTitle}</h2>
                    <div className="flex items-center space-x-2">
                      <Pin className="w-4 h-4 text-muted-foreground" />
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="space-y-1">
                    {(currentItem.children as readonly NavigationChild[]).map(
                      (child: NavigationChild) => {
                        const childLabel = tNavigation(`${child.translationKey}.label`, {
                          defaultValue: tNavigation(`${child.translationKey}.title`),
                        })
                        return (
                          <div
                            key={child.id}
                            className="flex items-center px-3 py-2 rounded-lg cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                            onClick={() => handleNavigation(child.url)}
                          >
                            <span className="text-sm text-sidebar-foreground">{childLabel}</span>
                          </div>
                        )
                      }
                    )}
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      )}
      </div>
    </>
  )
}
